import React, { useState } from "react";
import axios from "axios";
import SectionHeading from "../components/SectionHeading";
import TechBackdrop from "../components/TechBackdrop";
import { company, services } from "../mock";
import { MapPin, Phone, Mail, Send, Clock, Building2, CheckCircle2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useToast } from "../hooks/use-toast";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ContactPage() {
  const { toast } = useToast();
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", company: "", phone: "", service: "", message: ""
  });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: "Missing details", description: "Name, email and message are required.", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast({ title: "Message sent", description: "We'll be in touch within one business day." });
      setForm({ name: "", email: "", company: "", phone: "", service: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong. Please try again or email sales@brainwave-group.com directly.";
      toast({ title: "Couldn't send message", description: detail, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <section className="relative pt-36 lg:pt-44 pb-8 overflow-hidden">
        <div className="absolute inset-0 dot-grid opacity-60" />
        <TechBackdrop variant="topology" opacity={0.07} className="hidden lg:block" />
        <div className="relative max-w-5xl mx-auto px-5 lg:px-8 text-center">
          <SectionHeading
            eyebrow="Contact"
            title="Talk to a senior engineer."
            description="Drop us a note about your estate, your challenge, or the project you're planning. A named engineer will respond within one business day."
          />
        </div>
      </section>

      <section className="py-10">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-5 space-y-4">
            {[
              { icon: MapPin, t: "Office", d: company.address },
              { icon: Phone, t: "Phone", d: company.phone, href: `tel:${company.phone}` },
              { icon: Mail, t: "Email", d: company.email, href: `mailto:${company.email}` },
              { icon: Clock, t: "Hours", d: company.hours },
              { icon: Building2, t: "Founded", d: `${company.founded} · IBM Business Partner` }
            ].map((v, i) => (
              <a
                key={i}
                href={v.href || "#"}
                onClick={(e) => !v.href && e.preventDefault()}
                className="block rounded-xl border border-slate-200 bg-white p-5 hover:border-brand-400 transition-colors"
              >
                <div className="flex gap-4">
                  <div className="h-10 w-10 rounded-lg bg-brand-50 text-brand-700 grid place-items-center flex-shrink-0">
                    <v.icon className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="text-[12px] uppercase tracking-[0.2em] text-slate-500">{v.t}</div>
                    <div className="mt-1 font-semibold text-slate-900">{v.d}</div>
                  </div>
                </div>
              </a>
            ))}

            <div className="rounded-2xl overflow-hidden border border-slate-200 bg-slate-50">
              <iframe
                title="Sandton map"
                src="https://www.openstreetmap.org/export/embed.html?bbox=28.04,-26.12,28.08,-26.08&amp;layer=mapnik&amp;marker=-26.1076,28.0567"
                className="w-full h-[260px] border-0"
                loading="lazy"
              />
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="relative">
              <div className="absolute -inset-4 rounded-[28px] bg-gradient-to-br from-brand-400/20 via-transparent to-transparent blur-2xl" />
              <div className="relative rounded-3xl border border-slate-200 bg-white p-6 lg:p-8 shadow-xl">
                {sent ? (
                  <div className="text-center py-12">
                    <div className="mx-auto h-14 w-14 rounded-full bg-brand-50 grid place-items-center">
                      <CheckCircle2 className="h-7 w-7 text-brand-600" />
                    </div>
                    <h3 className="font-display mt-5 text-2xl font-semibold text-slate-900">Thanks — message received.</h3>
                    <p className="mt-2 text-slate-600">A senior engineer will respond within one business day.</p>
                    <Button onClick={() => setSent(false)} variant="outline" className="mt-6 rounded-full">Send another message</Button>
                  </div>
                ) : (
                  <form onSubmit={submit} className="grid sm:grid-cols-2 gap-5">
                    <div className="sm:col-span-1">
                      <Label htmlFor="name" className="text-[13px] text-slate-700">Full name *</Label>
                      <Input id="name" value={form.name} onChange={(e) => update("name", e.target.value)} className="mt-1.5 h-11 rounded-lg" placeholder="Jane Doe" />
                    </div>
                    <div className="sm:col-span-1">
                      <Label htmlFor="email" className="text-[13px] text-slate-700">Work email *</Label>
                      <Input id="email" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} className="mt-1.5 h-11 rounded-lg" placeholder="jane@company.com" />
                    </div>
                    <div className="sm:col-span-1">
                      <Label htmlFor="company" className="text-[13px] text-slate-700">Company</Label>
                      <Input id="company" value={form.company} onChange={(e) => update("company", e.target.value)} className="mt-1.5 h-11 rounded-lg" placeholder="ACME Pty Ltd" />
                    </div>
                    <div className="sm:col-span-1">
                      <Label htmlFor="phone" className="text-[13px] text-slate-700">Phone</Label>
                      <Input id="phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} className="mt-1.5 h-11 rounded-lg" placeholder="+27 ..." />
                    </div>
                    <div className="sm:col-span-2">
                      <Label className="text-[13px] text-slate-700">Service of interest</Label>
                      <Select value={form.service} onValueChange={(v) => update("service", v)}>
                        <SelectTrigger className="mt-1.5 h-11 rounded-lg">
                          <SelectValue placeholder="Select a service" />
                        </SelectTrigger>
                        <SelectContent>
                          {services.map((s) => (
                            <SelectItem key={s.id} value={s.title}>{s.title}</SelectItem>
                          ))}
                          <SelectItem value="Other">Something else</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="sm:col-span-2">
                      <Label htmlFor="msg" className="text-[13px] text-slate-700">Message *</Label>
                      <Textarea id="msg" value={form.message} onChange={(e) => update("message", e.target.value)} className="mt-1.5 min-h-[140px] rounded-lg" placeholder="Tell us about your estate or the challenge you're facing..." />
                    </div>
                    <div className="sm:col-span-2 flex items-center justify-between gap-4">
                      <div className="text-[12px] text-slate-500">We respond within one business day.</div>
                      <Button type="submit" disabled={submitting} className="rounded-full h-11 px-6 bg-brand-700 hover:bg-brand-800 text-white disabled:opacity-70">
                        {submitting ? "Sending…" : (<>Send message <Send className="ml-2 h-4 w-4" /></>)}
                      </Button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="h-16" />
    </>
  );
}
